import type { ObjectDirective } from "vue"

export const SAFE_CLICK_LOCK_MS = 800

export type SafeClickHandler = (_event: MouseEvent) => unknown | Promise<unknown>

export interface SafeClickOptions {
  handler?: SafeClickHandler
  lockMs?: number
  lockUntilSettled?: boolean
}

type SafeClickBinding = SafeClickOptions | SafeClickHandler | undefined

interface SafeClickState {
  locked: boolean
  unlockTimer?: ReturnType<typeof setTimeout>
  options: SafeClickOptions
  listener: (_event: MouseEvent) => void
}

const stateMap = new WeakMap<HTMLElement, SafeClickState>()

const normalizeOptions = (value: SafeClickBinding): SafeClickOptions => {
  if (typeof value === "function") {
    return {
      handler: value,
      lockUntilSettled: true,
    }
  }

  return {
    lockMs: SAFE_CLICK_LOCK_MS,
    lockUntilSettled: false,
    ...value,
  }
}

const isElementDisabled = (el: HTMLElement) => {
  const target = el.closest("button") ?? el
  return (
    target.getAttribute("disabled") !== null ||
    target.getAttribute("aria-disabled") === "true" ||
    target.classList.contains("is-disabled") ||
    target.classList.contains("is-loading")
  )
}

const unlockLater = (el: HTMLElement, lockMs: number) => {
  const state = stateMap.get(el)
  if (!state) return

  if (state.unlockTimer) {
    clearTimeout(state.unlockTimer)
  }

  state.unlockTimer = setTimeout(() => {
    const currentState = stateMap.get(el)
    if (!currentState) return

    currentState.locked = false
    currentState.unlockTimer = undefined
  }, lockMs)
}

export const vSafeClick: ObjectDirective<HTMLElement, SafeClickBinding> = {
  beforeMount(el, binding) {
    const state: SafeClickState = {
      locked: false,
      options: normalizeOptions(binding.value),
      listener: (event: MouseEvent) => {
        if (event.button !== 0 || isElementDisabled(el)) {
          return
        }

        const lockMs = state.options.lockMs ?? SAFE_CLICK_LOCK_MS
        if (state.locked) {
          event.preventDefault()
          event.stopImmediatePropagation()
          return
        }

        state.locked = true

        if (!state.options.handler) {
          unlockLater(el, lockMs)
          return
        }

        event.preventDefault()
        event.stopImmediatePropagation()

        try {
          const result = state.options.handler(event)
          if (state.options.lockUntilSettled && result && typeof (result as Promise<unknown>).finally === "function") {
            ;(result as Promise<unknown>).finally(() => unlockLater(el, lockMs))
          } else {
            unlockLater(el, lockMs)
          }
        } catch (error) {
          unlockLater(el, lockMs)
          throw error
        }
      },
    }

    stateMap.set(el, state)
    el.addEventListener("click", state.listener, true)
  },
  updated(el, binding) {
    const state = stateMap.get(el)
    if (state) {
      state.options = normalizeOptions(binding.value)
    }
  },
  unmounted(el) {
    const state = stateMap.get(el)
    if (!state) return

    if (state.unlockTimer) {
      clearTimeout(state.unlockTimer)
    }
    el.removeEventListener("click", state.listener, true)
    stateMap.delete(el)
  },
}
